/**
 * The System Health indicator: a badge in the navigation and, on click, one
 * row per dependency and the build (contracts/operator-api.yaml,
 * /system-health). The same shape and behaviour as TIM's console.
 *
 * Every row but one is coloured by the service, which states its rules in
 * logic/system_health.py and sums them as `overall`. The one coloured here is
 * the round trip of the request itself, because only the console can measure
 * it: an error when there is no answer, a warning above a second. The badge
 * shows the worse of that row and `overall`.
 *
 * An answer that failed shows that row alone. The rows of the answer before
 * it would be readings of a moment that has passed, shown as though current.
 */

import { useEffect, useState, type ReactElement } from 'react'
import {
  Body1Strong,
  Button,
  Caption1,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  CheckmarkCircle16Filled,
  CircleHalfFill16Regular,
  DismissCircle16Filled,
  Warning16Filled,
} from '@fluentui/react-icons'
import {
  ApiError,
  api,
  type BuildInfo,
  type HealthStatus,
  type SystemHealth,
  type SystemHealthRow,
} from '../api'
import { MonoText, statusToken } from '../platform'
import type { StatusPlacement } from './ConsoleNav'

export const REFRESH_MS = 30_000
export const SLOW_ROUND_TRIP_MS = 1000
/** An answer later than this is no answer. Without it a request that hangs
 *  holds the one-at-a-time guard, nothing is asked again, and the badge keeps
 *  saying what it said before the hang. */
export const ANSWER_WITHIN_MS = 10_000

export const BADGE: Record<HealthStatus, string> = {
  ok: 'Nominal',
  warning: 'Degraded',
  error: 'Down',
}

const RANK: Record<HealthStatus, number> = { ok: 0, warning: 1, error: 2 }

const FROM_OVERALL: Record<SystemHealth['overall'], HealthStatus> = {
  nominal: 'ok',
  degraded: 'warning',
  down: 'error',
}

export interface HealthReading {
  rows: SystemHealthRow[]
  /** The badge: the worse of the round trip and the service's `overall`. */
  status: HealthStatus
  build: BuildInfo | null
}

function backend(status: HealthStatus, value: string): SystemHealthRow {
  return { id: 'api', name: 'Backend API', status, value }
}

export function backendAnswered(roundTripMs: number): SystemHealthRow {
  const ms = `${Math.round(roundTripMs)} ms`
  return roundTripMs > SLOW_ROUND_TRIP_MS ? backend('warning', `slow, ${ms}`) : backend('ok', ms)
}

export function backendFailed(failure: unknown): SystemHealthRow {
  return failure instanceof ApiError
    ? backend('error', `answered ${failure.problem.status}`)
    : backend('error', 'no answer')
}

export function worst(statuses: HealthStatus[]): HealthStatus {
  return statuses.reduce<HealthStatus>((found, s) => (RANK[s] > RANK[found] ? s : found), 'ok')
}

/** What an answer says, with the round trip added. An `overall` this
 *  console does not know is read as down, never as nominal. */
export function readAnswer(answer: SystemHealth, roundTripMs: number): HealthReading {
  const api = backendAnswered(roundTripMs)
  return {
    rows: [api, ...answer.rows],
    status: worst([api.status, FROM_OVERALL[answer.overall] ?? 'error']),
    build: answer.build,
  }
}

/** Asks once at mount and every REFRESH_MS, never two at a time. */
export function useSystemHealth(): HealthReading | null {
  const [reading, setReading] = useState<HealthReading | null>(null)

  useEffect(() => {
    let live = true
    let asking = false
    const ask = async () => {
      if (asking) return
      asking = true
      const started = performance.now()
      let late: ReturnType<typeof setTimeout> | undefined
      try {
        const answer = await Promise.race([
          api.get<SystemHealth>('/system-health'),
          new Promise<never>((_, reject) => {
            late = setTimeout(() => reject(new Error('no answer in time')), ANSWER_WITHIN_MS)
          }),
        ])
        const read = readAnswer(answer, performance.now() - started)
        if (live) setReading(read)
      } catch (failure) {
        if (live) setReading({ rows: [backendFailed(failure)], status: 'error', build: null })
      } finally {
        clearTimeout(late)
        asking = false
      }
    }
    void ask()
    const timer = setInterval(ask, REFRESH_MS)
    return () => {
      live = false
      clearInterval(timer)
    }
  }, [])

  return reading
}

const useStyles = makeStyles({
  full: {
    width: '100%',
    justifyContent: 'flex-start',
  },
  triggerText: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  word: {
    fontWeight: tokens.fontWeightSemibold,
  },
  touch: {
    width: '44px',
    minWidth: '44px',
    height: '44px',
  },
  ok: { color: statusToken.healthy },
  warning: { color: statusToken.degraded },
  error: { color: statusToken.offline },
  waiting: { color: tokens.colorNeutralForeground3 },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    // Wide enough for the whole commit and its environment on one line.
    width: '400px',
    // The surface's own padding and a 16 px gutter each side, on a phone.
    maxWidth: 'calc(100vw - 64px)',
    boxSizing: 'border-box',
  },
  rows: {
    display: 'grid',
    gridTemplateColumns: 'auto auto 1fr',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  row: {
    display: 'contents',
  },
  icon: {
    display: 'flex',
  },
  value: {
    justifySelf: 'end',
    textAlign: 'end',
    color: tokens.colorNeutralForeground3,
  },
  build: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: tokens.spacingHorizontalS,
    rowGap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  buildLabel: {
    color: tokens.colorNeutralForeground3,
  },
  commit: {
    overflowWrap: 'anywhere',
  },
})

function StatusIcon({ status }: { status: HealthStatus | null }): ReactElement {
  const styles = useStyles()
  if (status === 'ok') return <CheckmarkCircle16Filled className={styles.ok} />
  if (status === 'warning') return <Warning16Filled className={styles.warning} />
  if (status === 'error') return <DismissCircle16Filled className={styles.error} />
  return <CircleHalfFill16Regular className={styles.waiting} />
}

export function SystemHealthPanel({ reading }: { reading: HealthReading | null }) {
  const styles = useStyles()
  return (
    <div className={styles.panel}>
      <Body1Strong>System health</Body1Strong>
      {reading ? (
        <div className={styles.rows}>
          {reading.rows.map((row) => (
            <div key={row.id} className={styles.row} data-health-row={row.id}>
              <span className={styles.icon} role="img" aria-label={BADGE[row.status]}>
                <StatusIcon status={row.status} />
              </span>
              <Caption1>{row.name}</Caption1>
              <Caption1 className={styles.value}>{row.value}</Caption1>
            </div>
          ))}
        </div>
      ) : (
        <Caption1>Asking the service</Caption1>
      )}
      {reading?.build ? (
        <div className={styles.build}>
          <Caption1 className={styles.buildLabel}>Version</Caption1>
          <MonoText>{reading.build.version ?? 'not stamped'}</MonoText>
          <Caption1 className={styles.buildLabel}>Commit</Caption1>
          <MonoText className={styles.commit}>
            {(reading.build.commit ?? 'not stamped') +
              (reading.build.environment ? ` · ${reading.build.environment}` : '')}
          </MonoText>
        </div>
      ) : null}
    </div>
  )
}

export function SystemHealthIndicator({ placement }: { placement: StatusPlacement }) {
  const styles = useStyles()
  const reading = useSystemHealth()
  const status = reading ? reading.status : null
  const word = status ? BADGE[status] : 'Checking'
  const iconOnly = placement !== 'rail'

  return (
    <Popover
      positioning={{
        position: placement === 'top-bar' ? 'below' : 'after',
        align: placement === 'top-bar' ? 'end' : 'bottom',
        overflowBoundaryPadding: 16,
      }}
      withArrow
    >
      <PopoverTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          className={mergeClasses(iconOnly ? undefined : styles.full, placement === 'top-bar' && styles.touch)}
          icon={<StatusIcon status={status} />}
          aria-label={iconOnly ? `System health: ${word}` : undefined}
        >
          {iconOnly ? undefined : (
            <span className={styles.triggerText}>
              <span>System health</span>
              <span className={mergeClasses(styles.word, status ? styles[status] : styles.waiting)}>{word}</span>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverSurface aria-label="System health">
        <SystemHealthPanel reading={reading} />
      </PopoverSurface>
    </Popover>
  )
}
